import { Link, useParams } from 'react-router-dom';

import { GetSubsetsResponseDto } from '../../../../../../../backend/src/case/subset/dto/get-subsets.response.dto';
import { RecentSubset } from '../../../../../api/types/backend-dto-types';
import formatDate from '@/helpers/formatDate';
import { Icon } from '@/new/components/icon';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/new/components/shadcn/empty';

export function RecentSubsets({
  recentlyAccessedSubsets,
  subsets,
}: {
  recentlyAccessedSubsets: RecentSubset[];
  subsets: GetSubsetsResponseDto[];
}) {
  const { matterId } = useParams();
  if (!matterId) return;

  // Filter out all documents subset from recently accessed
  const filteredRecentlyAccessedSubsets = recentlyAccessedSubsets.filter((subset) => !subset.case.isAllDocuments);

  // Get the 3 most recently created subsets (excluding all documents)
  const recentlyCreatedSubsets = subsets
    .filter((subset) => !subset.isAllDocuments)
    .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())
    .slice(0, 3);

  return (
    <div className="flex h-fit grow flex-col rounded-lg border border-gray-200 bg-gray-50 p-3">
      <h2 className="mt-1 mb-3 flex items-center gap-2 px-1 font-medium text-gray-600">
        <Icon className="lucide--group" />
        Recent Subsets
      </h2>
      <div className="flex flex-col gap-2">
        {filteredRecentlyAccessedSubsets.length > 0 ? (
          filteredRecentlyAccessedSubsets.map((subset) => (
            <Link key={subset.caseId} to={`/matters/${matterId}/subsets/${subset.caseId}`}>
              <div className="group flex items-center justify-between rounded-md border border-gray-200 bg-white p-3 px-4 transition-all hover:border-gray-300">
                <div>
                  <p className="flex items-center gap-2 font-medium text-gray-900">
                    <Icon className="lucide--group text-gray-500" />
                    {subset.case.name}
                  </p>

                  <div className="mt-1 flex items-center gap-3 text-xs text-gray-500">
                    <p>Last accessed {formatDate(subset.accessedAt)}</p>
                  </div>
                </div>

                <Icon className="lucide--arrow-right mr-3 text-gray-400 transition-transform group-hover:translate-x-1 group-hover:text-teal-500" />
              </div>
            </Link>
          ))
        ) : recentlyCreatedSubsets.length > 0 ? (
          recentlyCreatedSubsets.map((subset) => (
            <Link key={subset.id} to={`/matters/${matterId}/subsets/${subset.id}`}>
              <div className="group flex items-center justify-between rounded-md border border-gray-200 bg-white p-3 px-4 transition-all hover:border-gray-300">
                <div>
                  <p className="flex items-center gap-2 font-medium text-gray-900">
                    <Icon className="lucide--group text-gray-500" />
                    {subset.name}
                  </p>

                  <div className="mt-1 flex items-center gap-3 text-xs text-gray-500">
                    <p>Created on {formatDate(subset.createdDate)}</p>
                  </div>
                </div>

                <Icon className="lucide--arrow-right mr-3 text-gray-400 transition-transform group-hover:translate-x-1 group-hover:text-teal-500" />
              </div>
            </Link>
          ))
        ) : (
          <Empty className="rounded-lg border border-dashed border-gray-200 bg-white py-8">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Icon className="lucide--group" />
              </EmptyMedia>
              <EmptyTitle className="text-base">No Subsets yet</EmptyTitle>
              <EmptyDescription>Create a Subset to organize and filter your documents.</EmptyDescription>
            </EmptyHeader>
          </Empty>
        )}
      </div>
    </div>
  );
}
